import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Col, FormFeedback, Input, Label, Form } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiStar, mdiClockTimeFourOutline, mdiChevronLeft } from '@mdi/js';
import {
  applyCompOff,
  fetchCompOffDetails,
  fetchCompOffByTransNo,
} from '../../../store/apis/compOffService';
import {
  ErrorTable,
  FileUploadDropzone,
  TimePicker,
  DatePicker,
  ErrorModalWrapper,
} from '../../../components/Common';
import { DateUtil, StringUtil } from '../../../utils';
import {
  initialValues,
  validationSchema,
  populateFormValues,
} from './constants';
import { downloadBlobType } from '../../../store/apis/commonService';
import { getCompanyCode } from '../../../config/axios.config';
import { companySet1 } from '../constants';

import './ApplyCompOff.scss';
import '../index.scss';

function ApplyCompOff() {
  document.title = 'Apply Comp Off | Envision - Daikin';
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { CoCd, ID } = Object.fromEntries(query);
  const [errorModal, setErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);

  const fileInputRef = useRef(null);
  const formType = ID ? 'view' : 'apply';
  const selectedCoCd = getCompanyCode();

  const toggleErrorModal = () => setErrorModal(!errorModal);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });
  const watchValues = watch();

  const calculateHours = (fromTime, toTime) => {
    if (!fromTime || !toTime) {
      return 0;
    }
    if (fromTime && toTime) {
      const from = new Date(`1970-01-01T${fromTime}:00`);
      const to = new Date(`1970-01-01T${toTime}:00`);
      const diff = (to - from) / (1000 * 60 * 60);
      return (Math.round(diff * 100) / 100).toFixed(2);
    }
    return 0;
  };

  const calculateDays = hrs => {
    const h = parseFloat(hrs);
    let days;
    if (h <= 3) {
      days = 0;
    } else if (h <= 6) {
      days = 0.5;
    } else {
      days = 1;
    }
    return days.toFixed(2);
  };

  const { mutate: applyCompOffMutation, isPending: applyComOffPending } =
    useMutation({
      mutationFn: payload => applyCompOff(payload),
      onSuccess: data => {
        if (data.flag === 'True') {
          toast.success(
            `Comp Off Request #${data?.transactionNo} Has Been Submitted Successfully`,
            {
              className: 'toast-success',
            },
          );
          reset();
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else if (data.flag === 'False') {
          if (data?.message === 'CompOff cannot be applied on working day') {
            data.isTable = false;
          } else {
            data.isTable = true;
          }
          setErrorModal(true);
          setErrorData(data);
        }
      },
    });

  const { mutate: compOffDetailsMutate } = useMutation({
    mutationFn: payload => fetchCompOffDetails(payload),
    onSuccess: (data, params) => {
      if (data) {
        if (data.length === 0) {
          toast.error(
            `Timing information not available for ${params?.date} in access control device`,
            {
              className: 'toast-error',
            },
          );
          setValue('fromTime', '', {
            shouldValidate: true,
          });
          setValue('toTime', '', {
            shouldValidate: true,
          });
          return;
        }
        if (data.length > 0) {
          if (data[0]?.TimeIn === null || data[0]?.TimeOut === null) {
            toast.error(
              `Timing information not available for ${params?.date} in access control device`,
              {
                className: 'toast-error',
              },
            );
            setValue('fromTime', '', {
              shouldValidate: true,
            });
            setValue('toTime', '', {
              shouldValidate: true,
            });
          } else {
            const fromTime = DateUtil.convertTimeToHTMLTime(data[0]?.TimeIn);
            const toTime = DateUtil.convertTimeToHTMLTime(data[0]?.TimeOut);
            setValue('fromTime', fromTime, {
              shouldValidate: true,
            });
            setValue('toTime', toTime, {
              shouldValidate: true,
            });
          }
        }
      }
    },
  });

  const { data: compOffData } = useQuery({
    queryKey: ['leaveData', ID],
    queryFn: () =>
      fetchCompOffByTransNo({
        transNo: ID,
        companyCode: CoCd,
      }),
    enabled: formType === 'view',
  });

  const downloadFileMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const onSubmit = async values => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'file' && value && value.length > 0) {
        value.forEach(file => formData.append(key, file));
      } else {
        formData.append(key, value || '');
      }
    });
    const compOffDateHTML = DateUtil.formatDateObjToHtmlDate(
      values.compOffDate,
    );

    const compOffDate = `${compOffDateHTML} 00:00:00`;
    const leaveFromDate = `${compOffDateHTML} ${values.fromTime}:00`;
    const leaveToDate = `${compOffDateHTML} ${values.toTime}:00`;

    formData.set('compOffDate', compOffDate);
    formData.set('leaveFromDate', leaveFromDate);
    formData.set('leaveToDate', leaveToDate);
    formData.append('hrs', values.hrs || '');
    formData.append('leaveDays', values.leaveDays || '');

    applyCompOffMutation(formData);
  };

  const handleAutoFill = async () => {
    if (!watchValues?.compOffCategory === 'In Office') {
      return;
    }

    if (!watchValues?.compOffDate) {
      toast.error('Please select Comp Off Date', {
        className: 'toast-error',
      });
    }

    compOffDetailsMutate({
      date: watchValues?.compOffDate,
    });
  };

  useEffect(() => {
    if (watchValues?.compOffCategory === 'In Office' && formType !== 'view') {
      setValue('fromTime', '');
      setValue('toTime', '');
    }
  }, [watchValues?.compOffCategory, setValue]);

  useEffect(() => {
    if (formType === 'view' && compOffData && compOffData.length > 0) {
      const editFormValues = populateFormValues(compOffData?.[0]);
      reset(editFormValues);

      const raw = editFormValues.attachmentPath;
      if (!raw || raw === '-' || raw.trim() === '') return;

      let attachmentArray = [];
      attachmentArray = StringUtil.checkStringOrJsonArray(raw);
      (async () => {
        try {
          const files = await Promise.all(
            attachmentArray.map(async fileName => {
              const response = await downloadFileMutation.mutateAsync({
                fileName,
                type: 'CompOff',
              });
              return new File([response], fileName, { type: response.type });
            }),
          );
          setValue('file', files);
          if (fileInputRef.current) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(files);
            fileInputRef.current.files = dataTransfer.files;
          }
        } catch (err) {
          toast.error('Error downloading attachments. Please try again.', {
            className: 'toast-error',
          });
        }
      })();
    }
  }, [formType, compOffData, reset]);

  useEffect(() => {
    if (watchValues?.fromTime && watchValues?.toTime) {
      const hrs = calculateHours(watchValues.fromTime, watchValues.toTime);
      setValue('hrs', hrs, {
        shouldValidate: true,
      });
      const leaveDays = calculateDays(hrs);
      setValue('leaveDays', leaveDays, {
        shouldValidate: true,
      });
    } else {
      setValue('hrs', '');
      setValue('leaveDays', '');
    }
  }, [watchValues.fromTime, watchValues.toTime, setValue]);

  useEffect(() => {
    if (errorData && !errorData?.isTable) {
      toast.error(errorData?.message || 'An error occurred', {
        className: 'toast-error',
      });
    }
  }, [errorData]);

  return (
    <div className="page-content px-0" style={{ paddingTop: '70px' }}>
      <div className="d-flex flex-row p-0">
        <div className="col-7 p-0 h-100">
          <div className="d-flex flex-row align-items-center justify-content-between leave-layout-border-bottom">
            {formType !== 'view' ? (
              <div className="ps-3 py-3 d-flex align-items-center fs-5 fw-semibold apply-header">
                Apply Comp Off
              </div>
            ) : (
              <div className="ps-1 py-3 d-flex flex-row align-items-center gap-2 apply-header">
                <button
                  type="button"
                  className="btn btn-link p-0 back-btn"
                  onClick={() => window.history.back()}
                >
                  <Icon path={mdiChevronLeft} size={1.1} />
                  <span className="back fs-5">Back</span>
                </button>
                <div
                  style={{
                    width: '1px',
                    borderLeft: '1px solid lightgrey',
                    height: '20px',
                  }}
                />
                <div className="d-flex flex-row gap-1">
                  <span className="request-id-span fs-5">Request ID</span>
                  <span className="request-id-span-value fs-5">{ID}</span>
                </div>
              </div>
            )}
          </div>
          <Form
            className="d-flex flex-column gap-2 p-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div>
              <Label for="compOffCategory" className="my-1">
                <span>Comp Off Category</span>
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="compOffCategory"
                control={control}
                render={({ field }) => (
                  <Input
                    className={`form-select external-filter-input ${
                      errors.compOffCategory && 'is-invalid'
                    }`}
                    type="select"
                    name="compOffCategory"
                    {...field}
                    disabled={formType === 'view'}
                    title="Select the applicable category for your Comp Off from the available dropdown options"
                  >
                    <option value="">Select Comp Off Category</option>
                    <option value="In Office">In Office</option>
                    <option value="Business Trip">Business Trip</option>
                  </Input>
                )}
              />

              {errors.compOffCategory && (
                <FormFeedback>{errors.compOffCategory.message}</FormFeedback>
              )}
            </div>
            <div>
              <Label for="reason" className="my-1">
                <span>Comp Off Reason</span>
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="textarea"
                    rows="3"
                    className={`form-control external-filter-input ${errors.reason && 'is-invalid'}`}
                    name="reason"
                    id="reason"
                    placeholder="Enter Reason"
                    disabled={formType === 'view'}
                    title="Enter a reason for requesting the Comp Off."
                  />
                )}
              />

              {errors.reason && (
                <FormFeedback>{errors.reason.message}</FormFeedback>
              )}
            </div>
            <div>
              <Label for="compOffDate" className="my-1">
                <span>Comp Off Date</span>
                <span className="text-danger">*</span>
              </Label>
              <div className="d-flex flex-row align-items-start gap-2">
                <div className="flex-grow-1">
                  <Controller
                    name="compOffDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        disabled={
                          formType === 'view' ||
                          (watchValues.fromTime &&
                            watchValues.toTime &&
                            watchValues.compOffCategory === 'In Office')
                        }
                        className={`external-filter-input ${errors.compOffDate ? 'is-invalid' : ''}`}
                        error={!!errors.compOffDate}
                        title="Select the date on which you worked and are applying Comp Off for."
                      />
                    )}
                  />
                  {errors.compOffDate && (
                    <div className="invalid-feedback d-block">
                      {errors.compOffDate.message}
                    </div>
                  )}
                </div>
                <Button
                  className=" external-filter-input d-flex flex-row align-items-center gap-1"
                  style={{
                    background: 'Transparent',
                    border: '1px solid #017EBD',
                    color: '#017EBD',
                    cursor:
                      watchValues?.compOffCategory !== 'In Office' && 'pointer',
                  }}
                  disabled={
                    watchValues?.compOffCategory !== 'In Office' ||
                    formType === 'view'
                  }
                  onClick={handleAutoFill}
                >
                  <Icon path={mdiClockTimeFourOutline} size={0.7} />
                  <span
                    title={`Automatically fetch the 'From Time' and 'To Time' for the selected Comp Off date from existing biometric data, if available.
Applicable for 'In Office' Comp Off category.`}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Autofill
                  </span>
                </Button>
              </div>
            </div>
            <div className="d-flex flex-row gap-2">
              <Col>
                <div>
                  <Label for="fromTime" className="my-1">
                    <span>From Time</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="fromTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        interval={15}
                        disabled={
                          watchValues?.compOffCategory === 'In Office' ||
                          formType === 'view'
                        }
                        className={`mpi-time-picker external-filter-input ${errors.fromTime ? 'is-invalid' : ''} time-picker-pointer`}
                        error={!!errors.fromTime}
                        title="Enter the start time of the extra hours worked."
                      />
                    )}
                  />
                  {errors.fromTime && (
                    <div className="invalid-feedback d-block">
                      {errors.fromTime.message}
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div>
                  <Label for="toTime" className="my-1">
                    <span>To Time</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="toTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        interval={15}
                        disabled={
                          watchValues?.compOffCategory === 'In Office' ||
                          formType === 'view'
                        }
                        className={`mpi-time-picker external-filter-input ${errors.toTime ? 'is-invalid' : ''} time-picker-pointer`}
                        error={!!errors.toTime}
                        title="Enter the end time of the extra hours worked."
                      />
                    )}
                  />
                  {errors.toTime && (
                    <div className="invalid-feedback d-block">
                      {errors.toTime.message}
                    </div>
                  )}
                </div>
              </Col>
            </div>
            <div className="d-flex flex-row gap-2">
              <Col>
                <div>
                  <Label className="my-1">
                    <span>Hours</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="hrs"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        value={watchValues.hrs || ''}
                        className={`form-control external-filter-input ${errors.hrs && 'is-invalid'}`}
                        placeholder="Enter Hours"
                        readOnly
                        disabled
                        title="Auto-calculated – Total number of hours worked."
                      />
                    )}
                  />
                  {errors.hrs && (
                    <div className="invalid-feedback d-block">
                      {errors.hrs.message}
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div>
                  <Label className="my-1">
                    <span>Days</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="leaveDays"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        value={watchValues.leaveDays || ''}
                        className={`form-control external-filter-input ${errors.leaveDays && 'is-invalid'}`}
                        placeholder="Enter Days"
                        readOnly
                        disabled
                        title="Auto-calculated – Total number of days calculated based on the hours worked."
                      />
                    )}
                  />
                  {errors.leaveDays && (
                    <div className="invalid-feedback d-block">
                      {errors.leaveDays.message}
                    </div>
                  )}
                </div>
              </Col>
            </div>
            <div>
              <Label for="file" className="my-1">
                <span>Upload Attachment</span>
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="file"
                control={control}
                render={({ field }) => (
                  <FileUploadDropzone
                    error={errors.file}
                    onDrop={async acceptedFiles => {
                      try {
                        await validationSchema.fields.file.validate(
                          acceptedFiles,
                        );
                        clearErrors('files');
                        setValue('file', acceptedFiles, {
                          shouldValidate: true,
                        });
                      } catch (err) {
                        setError('file', {
                          type: 'manual',
                          message: err.message,
                        });
                      }
                    }}
                    selectedFiles={field.value}
                    className={errors.file ? 'is-invalid' : ''}
                    handleDeleteFile={file => {
                      field.onChange(
                        field.value.filter(f => f.name !== file.name),
                      );
                    }}
                    disabled={formType === 'view'}
                    showDeleteButton={formType !== 'view'}
                    tooltipTitle="Upload any required proof"
                    showDownloadButton={formType === 'view'}
                    multiple
                  />
                )}
              />
              {errors.file && (
                <FormFeedback>{errors.file.message}</FormFeedback>
              )}
            </div>

            {formType !== 'view' && (
              <div className="d-flex flex-row align-items-center justify-content-center gap-2 p-3">
                <Button
                  className="custom-light-btn"
                  style={{
                    width: '25%',
                  }}
                  onClick={() => reset(initialValues)}
                  disabled={formType === 'view'}
                >
                  Clear
                </Button>
                <Button
                  className="custom-btn"
                  style={{
                    width: '25%',
                  }}
                  type="submit"
                  disabled={
                    !isValid || formType === 'view' || applyComOffPending
                  }
                >
                  <span>{applyComOffPending ? 'Submitting...' : 'Submit'}</span>
                </Button>
              </div>
            )}
            {errorData && errorData?.isTable && (
              <ErrorModalWrapper
                isModalOpen={errorModal}
                toggle={toggleErrorModal}
                errorMessage={errorData?.message}
                onButtonClick={toggleErrorModal}
                size={errorData?.isTable ? 'xl' : 'md'}
              >
                {errorData?.isTable && (
                  <ErrorTable errorData={errorData?.errorData} />
                )}
              </ErrorModalWrapper>
            )}
          </Form>
        </div>
        <div
          className="col-5 leave-layout-border-left"
          style={{
            color: 'black',
          }}
        >
          <div className="tnc-wrapper p-3">
            <b className="tnc-title mb-1">Terms & Conditions</b>
            <ul>
              <li>
                Compensatory days off must be used within the calendar year it
                is applied and can be taken in conjunction with annual leave.
                Comp off will be forfeited if employee fails to use it within
                the allowed time frame and cannot be converted into overtime
                payment. Compensatory Day Off is limited to days spent working
                during weekend and public holidays.
              </li>
              <li>
                Comp off can only be applied for in the same calendar year as
                when it took place. Any Comp off applications applied for in the
                next calendar year or anytime thereafter will not be considered
                or approved.
              </li>
              <li>No further extension will be considered.</li>
              <li>
                The employee should physically be in the office to be eligible
                to claim for comp off except when an employee is on business
                trip. Employee will be required to punch in and punch out in the
                attendance registration system when working during weekend and
                public holiday. Attendance report will be checked when
                requesting for comp off application.
              </li>
            </ul>
            <b>Below guideline should be followed when claiming comp off.</b>
            <ul>
              <li>
                No Comp off: &lt;= 3 working hours (less than or equal to 3
                working hours)
              </li>
              <li>
                Half Day Comp off claim: &gt; 3 hours but &lt;= 6 working hours
              </li>
              <li>One Day Comp Off claim: &gt; 6 working hours</li>
            </ul>
            <p>
              STRICTLY NO COMP OFF WILL BE CONSIDERED WITHOUT BIOMETRIC
              REGISTRATION. No manual time in/out registration will be
              entertained.
            </p>
            {companySet1.includes(selectedCoCd) && (
              <div className="d-flex flex-row align-items-center gap-1">
                <Icon path={mdiStar} size={1} color="#DC6803" />
                <span>Please</span>
                <a
                  href="https://denv.sharepoint.com/sites/eu-intra-dame-hr-en/SitePages/Compensatory-Off.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clickable"
                >
                  click here
                </a>
                <span>to view the leave policy in detail.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApplyCompOff;
